import { useState, useMemo, useCallback } from 'react';
import useSelectorWithProps from 'hooks/useSelectorWithProps';
import PropTypes from 'prop-types';
import { find, pipe, filter, first, get, keyBy } from 'lodash/fp';
import { Stack, InPlaceInput, Input, Select } from '@kinesis/bungle';
import { Content } from '@kinesis/bungle/legacy';
import { items, flatten, relabelItem } from 'data/attributes';
import FormSection from 'components/form-section/FormSection';
import settingsSelector from 'selectors/settingsSelector';
import { isItem, validSettings } from 'utils/settingsModal';
import GenericSettings from './attribute-settings.generic';
import SettingsGrid from './settings-grid';

const propTypes = {
  setting: PropTypes.object.isRequired,
  instantiation: PropTypes.object.isRequired,
  setSetting: PropTypes.func.isRequired,
  setInstantiation: PropTypes.func.isRequired,
  workspaceId: PropTypes.number.isRequired,
};

const defaultProps = {};

const description =
  'Services describe the benefits provided by amenities such as restaurants, parks, public transport, and others.';

const unitLabels = {
  'area-sqm': 'Area',
  count: 'Count',
};

const ServiceSettings = ({
  instantiation,
  setInstantiation,
  setSetting,
  setting,
  workspaceId,
}) => {
  const [selections, setSelections] = useState([]);

  const settingsSpecifications = useSelectorWithProps(settingsSelector, {
    attribute: 'service-v2',
    workspaceId,
  });

  const singleSelection = useMemo(
    () =>
      selections.length === 1
        ? pipe(flatten, filter({ key: first(selections) }), first)(setting)
        : undefined,
    [selections, setting],
  );

  const itemHeading = useMemo(
    () =>
      isItem(singleSelection)
        ? pipe(
            items,
            keyBy('key'),
            get([get('key', singleSelection), 'label']),
          )(instantiation)
        : undefined,
    [singleSelection, instantiation],
  );

  const onChangeItemLabel = useCallback(
    (value) => {
      if (singleSelection) {
        setInstantiation((current) =>
          relabelItem(singleSelection.key, value, current),
        );
      }
    },
    [setInstantiation, singleSelection],
  );

  const activeSettings = useMemo(
    () => validSettings(singleSelection, settingsSpecifications),
    [singleSelection, settingsSpecifications],
  );

  const withDivider = (children) => (
    <Content borderPlacement='bottom' padding='none' paddingBottom='large'>
      {children}
    </Content>
  );

  const itemType = useMemo(
    () =>
      pipe(
        flatten,
        find({ key: get('key', singleSelection) }),
        get('item_type'),
      )(instantiation),
    [instantiation, singleSelection],
  );

  const unitLabel = useMemo(
    () =>
      pipe(
        flatten,
        find({ key: get('key', singleSelection) }),
        get('item_type_label'),
      )(instantiation),
    [instantiation, singleSelection],
  );

  return (
    <GenericSettings
      description={description}
      instantiation={instantiation}
      selections={selections}
      setSelections={setSelections}
      setting={setting}
      workspaceId={workspaceId}
    >
      <FormSection key={get('key', singleSelection)}>
        <Stack space='medium'>
          {itemHeading && (
            <InPlaceInput
              onChange={onChangeItemLabel}
              placeholder='Item'
              value={itemHeading}
              variant='title'
            />
          )}
          {isItem(singleSelection) &&
            itemType !== 'count' &&
            withDivider(
              <Select
                label='Item type'
                onChange={console.log}
                disabled
                value={itemType}
                options={[
                  {
                    label: unitLabels[itemType],
                    value: singleSelection.unit.type,
                  },
                ]}
              />,
            )}
          {isItem(singleSelection) &&
            itemType === 'count' &&
            withDivider(
              <Input
                label='Units label'
                magnitude='large'
                value={unitLabel}
                onChange={console.log}
                disabled
                maxWidth={null}
              />,
            )}

          {isItem(singleSelection) && (
            <SettingsGrid
              item={singleSelection}
              setSetting={setSetting}
              settings={activeSettings}
              specifications={settingsSpecifications}
              workspaceId={workspaceId}
            />
          )}
        </Stack>
      </FormSection>
    </GenericSettings>
  );
};

ServiceSettings.defaultProps = defaultProps;
ServiceSettings.propTypes = propTypes;

export default ServiceSettings;
